import {Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {getAllEnumValues, getErrorMessage, Organisation} from '@cas-hub/acabim-common-client';
import {MessageService} from 'primeng/api';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {CurrentResourceUsage, SubscriptionResourceType} from '../../../../_models/resource-subscription.model';
import {ResourceSubscriptionService} from '../../../../_services/resource-subscription.service';

@Component({
    selector: 'acabim-admin-subscription-request',
    templateUrl: './subscription-request.component.html',
    styleUrls: ['./subscription-request.component.scss']
})
export class SubscriptionRequestComponent implements OnDestroy {

    @Input() public organisation?: Organisation;

    public get currentUsageInfo(): CurrentResourceUsage[] {
        return this._currentUsageInfo;
    }

    @Input() public set currentUsageInfo(val: CurrentResourceUsage[]) {
        this._currentUsageInfo = val;
        this.refreshData();
    }

    @Output() public resourceSubscriptionCreated = new EventEmitter();

    public availableResources: { name: string, id: number }[];
    public subscriptionRequestForm: FormGroup;

    public currentSeatCountForSelectedResource = 0;

    private _currentUsageInfo: CurrentResourceUsage[] = [];
    private readonly _unsubscribe = new Subject<void>();

    private get selectedResourceType(): SubscriptionResourceType {
        const value: { name: string, id: number } | undefined = this.subscriptionRequestForm?.get('resourceType')?.value;
        if (value && value.id > -1) {
            return value.id;
        }

        return SubscriptionResourceType.project;
    }

    public get inputSeatCount(): number {
        return Number(this.subscriptionRequestForm.get('numberOfResources')?.value ?? 0);
    }

    public get finalCount(): number {
        return this.currentSeatCountForSelectedResource + this.inputSeatCount;
    }

    public constructor(private readonly resourceSubscriptionService: ResourceSubscriptionService,
                       private readonly messageService: MessageService) {
        this.availableResources = [];
        for (const resourceType of getAllEnumValues(SubscriptionResourceType)) {
            this.availableResources.push({name: resourceType.charAt(0).toUpperCase() + resourceType.slice(1), id: Number(SubscriptionResourceType[resourceType as any])});
        }

        this.subscriptionRequestForm = new FormGroup({
            resourceType: new FormControl(this.availableResources[0], [Validators.required]),
            numberOfResources: new FormControl(0, [Validators.required])
        });

        this.subscriptionRequestForm.get('resourceType')?.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(_ => {
            this.refreshData();
        });

        this.refreshData();
    }

    public request(): void {
        if (this.organisation) {
            const resourceType = this.selectedResourceType;
            const inputSeatCount = this.inputSeatCount;
            this.resourceSubscriptionService.addOrRemoveUsageQuantity(this.organisation.id, resourceType, inputSeatCount).subscribe(_ => {
                this.subscriptionRequestForm.get('numberOfResources')?.setValue(0);
                this.resourceSubscriptionCreated.emit();
            }, error => {
                this.messageService.add(getErrorMessage(error, 'Resource Error', 'subscription-create-request'));
            });
        }
    }

    private refreshData(): void {
        const value =
            this.currentUsageInfo.find(x => x.resourceType.toLowerCase() === SubscriptionResourceType[this.selectedResourceType].toLowerCase())?.allowedResources ?? undefined;
        if (value !== undefined) {
            this.currentSeatCountForSelectedResource = value;
        }
    }

    public ngOnDestroy(): void {
        this._unsubscribe.next();
        this._unsubscribe.complete();
    }
}
